import { DisposableBag } from '../core/disposable.js';
import { adoptStyles } from '../core/adopt-styles.js';

/**
 * Base class for native Custom Elements (Shadow DOM).
 *
 * WHY:
 * - No UI framework. Components are platform Custom Elements with a shadow root.
 * - Styling uses **constructable stylesheets** (`adoptedStyleSheets`), which are
 *   programmatic CSSOM and therefore NOT subject to the prod CSP `style-src` —
 *   so the strict CSP needs no `'unsafe-inline'`.
 * - A {@link DisposableBag} ties subscriptions/listeners to the element lifecycle;
 *   `disconnectedCallback` disposes them — no leaks.
 */
export abstract class BaseElement extends HTMLElement {
  protected readonly root: ShadowRoot;
  protected readonly bag = new DisposableBag();

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  /** Adopt a CSS string onto this element's shadow root (CSP-clean in browsers). */
  protected adoptStyles(css: string): void {
    adoptStyles(this.root, css);
  }

  /** Replace shadow content. `markup` must already be safe (escaped) HTML. */
  protected setContent(markup: string): void {
    this.root.innerHTML = markup;
  }

  disconnectedCallback(): void {
    this.bag.dispose();
  }
}
